// src/Components/Profile/RenewModal.jsx
import React, { useEffect, useState, useContext } from "react";
import client from "../../api/client";
import { AuthContext } from "../../contextStore/AuthContext";
import formatToIST from "../../utils/formatToIST";

/**
 * Props:
 * - open (boolean)
 * - machineId (number)
 * - onClose()
 * - onSuccess(updatedMachine)  // called after successful renew
 */
export default function RenewModal({ open, machineId, onClose, onSuccess }) {
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [credits, setCredits] = useState([]);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const { token } = useContext(AuthContext) || {};

  useEffect(() => {
    if (!open) return;
    setError(null);
    setCredits([]);
    setLoadingCredits(true);

    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    client
      .get("/api/seller/post_credits", { headers })
      .then((res) => {
        if (res && res.data && res.data.ok) {
          setCredits(res.data.credits || []);
        } else {
          setError(
            (res && res.data && (res.data.error || res.data.message)) ||
              "Failed to load credits"
          );
        }
      })
      .catch((err) => {
        console.error("load credits err", err);
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load credits";
        setError(msg);
      })
      .finally(() => setLoadingCredits(false));
  }, [open, token]);

  if (!open) return null;

  async function handleUseCredit() {
    setProcessing(true);
    setError(null);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await client.post(
        `/api/machines/${machineId}/renew`,
        { type: "use_credit" },
        { headers }
      );

      if (res && res.data && res.data.ok && res.data.published) {
        // Prefer machine from response; fallback to visible_until or minimal object
        let updated = res.data.machine || null;
        if (!updated) {
          // if visible_until provided, construct a minimal update so UI can refresh expiry
          if (res.data.visible_until) {
            updated = { id: machineId, visible_until: res.data.visible_until };
          } else if (res.data.purchase_id || res.data.purchase_id === 0) {
            // still provide at least id in case frontend wants to reload
            updated = { id: machineId };
          }
        }

        onSuccess && onSuccess(updated);
        onClose && onClose();
        return;
      }

      // backend indicates no credits / needs payment
      const body = (res && res.data) || {};
      if (
        body &&
        (body.needs_payment ||
          body.requires_payment ||
          body.needs_payment === true ||
          body.reason === "no_post_credits")
      ) {
        if (window.confirm("No credits available. Go buy a seller plan?")) {
          window.location.href = "/plans";
        } else {
          onClose && onClose();
        }
        return;
      }

      throw new Error(
        body && (body.message || body.error)
          ? body.message || body.error
          : "Could not renew listing"
      );
    } catch (err) {
      console.error("renew err", err);
      const body = err?.response?.data || {};

      if (err?.response?.status === 401) {
        window.location.href = `/login?redirect=${encodeURIComponent(
          `/edit-listing/${machineId}`
        )}`;
        return;
      }

      if (err?.response?.status === 402 || body?.error === "no_post_credits") {
        // explicit handling: redirect to plans
        if (window.confirm("No credits available. Go buy a seller plan?")) {
          window.location.href = "/plans";
          return;
        }
      }

      setError(body?.message || err?.message || "Failed to renew listing");
    } finally {
      setProcessing(false);
    }
  }

  const first = credits && credits.length ? credits[0] : null;

  return (
    <div
      className="renew-modal fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.45)" }}
    >
      <div className="bg-white rounded p-4 shadow-lg w-full max-w-xl">
        <div className="flex items-center justify-between mb-3">
          <h3 style={{ margin: 0 }}>Renew Listing</h3>
          <button
            onClick={() => onClose && onClose()}
            style={{ border: "none", background: "transparent", fontSize: 18 }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div style={{ minHeight: 80 }}>
          {loadingCredits ? (
            <div>Loading available credits…</div>
          ) : error ? (
            <div style={{ color: "crimson" }}>{error}</div>
          ) : !credits || credits.length === 0 ? (
            <div>
              <p>No active post credits available.</p>
              <p>
                <button
                  onClick={() => {
                    window.location.href = "/plans";
                  }}
                  className="om-btn"
                >
                  Buy Seller Plan
                </button>
              </p>
            </div>
          ) : (
            <div>
              <p>
                Available credits ({credits.length}) — using the earliest one by
                default:
              </p>

              {/* show first credit */}
              {first && (
                <div
                  style={{
                    border: "1px solid #eee",
                    padding: 10,
                    borderRadius: 6,
                  }}
                >
                  <div>
                    <strong>Plan:</strong>{" "}
                    {first.plan_name || `Plan ${first.plan_id}`}
                  </div>
                  <div>
                    <strong>Duration:</strong>{" "}
                    {first.duration_days
                      ? `${first.duration_days} day${
                          first.duration_days > 1 ? "s" : ""
                        }`
                      : "Unlimited"}
                  </div>
                  <div>
                    <strong>Price:</strong>{" "}
                    {first.price_cents
                      ? `₹${(first.price_cents / 100).toFixed(2)}`
                      : "—"}
                  </div>
                  <div>
                    <strong>Purchased:</strong>{" "}
                    {credits[0].created_at
                      ? new Date(credits[0].created_at).toLocaleString(
                          "en-IN",
                          { timeZone: "Asia/Kolkata" }
                        )
                      : "-"}
                  </div>
                  <div>
                    <strong>Expires at:</strong>{" "}
                    {credits[0].expires_at
                      ? new Date(credits[0].expires_at).toLocaleString(
                          "en-IN",
                          { timeZone: "Asia/Kolkata" }
                        )
                      : "No expiry"}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-4 text-right">
          <button
            onClick={() => onClose && onClose()}
            className="om-btn ghost"
            style={{ marginRight: 8 }}
            disabled={processing}
          >
            Cancel
          </button>
          <button
            onClick={handleUseCredit}
            className="om-btn"
            disabled={
              processing || loadingCredits || !credits || credits.length === 0
            }
          >
            {processing ? "Processing…" : "Use Credit & Renew"}
          </button>
        </div>
      </div>
    </div>
  );
}
